// Join traversal: carry a hand-off's shared needle strings across the
// open-session hop, so the counterpart trace opens ON the shared content
// instead of at its own top.
//
// The lanes dock's "open session" stages the strings here under the
// COUNTERPART's trace id; the trace detail consumes them on its first
// hydration, scans its rendered turn blocks for each string, and feeds the
// matches to the same highlight surface fired signals use (marks + locator
// stops). The store is module memory on purpose: needle strings are tenant
// content (ids, hashes, paths -- token-shaped by construction), so they never
// ride a URL or any disk-backed storage. A modifier-click that opens a new
// browser tab simply lands without highlights, same as before this existed.

import { toolUseText } from "./signalHighlight.js";
import { toolResultText } from "./traceToolCall.js";

// One follow per counterpart trace: staging twice before the tab hydrates
// keeps the newest payload (the reader re-clicked), and consuming is
// one-shot so a later plain open of the same trace stays unmarked.
const staged = new Map();

const NEEDLE_CAP = 8;

export function stageJoinFollow(traceId, { needles = [], fromTraceId = null } = {}) {
  if (!traceId) return;
  const strs = [...new Set(needles.filter((n) => typeof n === "string" && n))].slice(0, NEEDLE_CAP);
  if (!strs.length) return;
  staged.set(traceId, { needles: strs, fromTraceId });
}

export function takeJoinFollow(traceId) {
  if (!traceId || !staged.has(traceId)) return null;
  const payload = staged.get(traceId);
  staged.delete(traceId);
  return payload;
}

// The needle can recur across a whole session (it may be the counterpart's
// working vocabulary -- its own paths, its own ids), and the locator pill is
// only useful while its stops are countable. Enough stops to prove the
// relationship; the census already proved the volume.
const LOCS_PER_NEEDLE = 12;
const LOCS_TOTAL = 60;

// The searchable text of a rendered block -- the SAME string the detail's
// block views hand to their mark renderer, because a mark is found again by
// plain substring search in that text (markRanges). Block types whose views
// don't consume the highlight map (thinking, tool_def trees) return null:
// a loc there would count a locator stop no mark can honor.
function blockText(block) {
  if (!block || typeof block !== "object") return null;
  if (block.type === "text") return typeof block.text === "string" ? block.text : null;
  if (block.type === "tool_use") return block.input != null ? toolUseText(block.input) : null;
  if (block.type === "tool_result") return toolResultText(block.content) || null;
  return null;
}

// Scan a trace's rendered turns for the staged needle strings and return
// fired-signal-shaped entries (one per needle that landed) for
// buildHighlightMap: { command, type, value: { locs: [{ surface: "block",
// turn, block, match }] } }. The probes that found the counterpart were
// case-insensitive (ILIKE), so the scan is too -- but `match` is sliced from
// the block's own text so the mark's exact-substring search lands.
export function scanTurnsForNeedles(turns, needles) {
  if (!Array.isArray(turns) || !Array.isArray(needles) || !needles.length) return [];
  const entries = [];
  let total = 0;
  for (const needle of needles) {
    if (typeof needle !== "string" || !needle || total >= LOCS_TOTAL) continue;
    const lower = needle.toLowerCase();
    const locs = [];
    for (let ti = 0; ti < turns.length && locs.length < LOCS_PER_NEEDLE && total < LOCS_TOTAL; ti++) {
      const blocks = Array.isArray(turns[ti]?.blocks) ? turns[ti].blocks : [];
      for (let bi = 0; bi < blocks.length && locs.length < LOCS_PER_NEEDLE && total < LOCS_TOTAL; bi++) {
        const text = blockText(blocks[bi]);
        if (!text) continue;
        const idx = text.toLowerCase().indexOf(lower);
        if (idx === -1) continue;
        locs.push({ surface: "block", turn: ti, block: bi, match: text.slice(idx, idx + needle.length) });
        total += 1;
      }
    }
    if (locs.length) entries.push({ command: "join.follow", type: "join.shared", value: { locs } });
  }
  return entries;
}
